'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';
import { cn } from '@helpers/styleUtils';
import { AiOutlineDoubleRight, AiOutlineDoubleLeft } from 'react-icons/ai';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        className={cn(
            'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className,
        )}
        aria-hidden="true"
        ref={ref}
        {...props}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

SheetOverlay.propTypes = {
    className: PropTypes.string,
};

const sheetVariants = cva('fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out', {
    variants: {
        side: {
            top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
            bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-md',
            right: 'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-md',
        },
    },
    defaultVariants: {
        side: 'right',
    },
});

const SheetContent = React.forwardRef(
    ({ side = 'right', className, children, onClose, ...props }, ref) => (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                ref={ref}
                aria-labelledby="sheet-title"
                aria-describedby="sheet-description"
                className={cn(sheetVariants({ side }), className)}
                {...props}
            >
                <SheetPrimitive.Close
                    onClick={() => {
                        if (onClose) {
                            onClose();
                        }
                    }}
                    className={cn(
                        'bg-primary-blue text-white text-xl absolute top-4 rounded-full ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none flex items-center justify-center w-8 h-8',
                        side === 'right' && 'left-[-15px]',
                        side === 'left' && 'right-[-15px]',
                    )}
                >
                    {side === 'right' && <AiOutlineDoubleRight />}
                    {side === 'left' && <AiOutlineDoubleLeft />}
                    <span className="sr-only">Close</span>
                </SheetPrimitive.Close>
                {children}
            </SheetPrimitive.Content>
        </SheetPortal>
    ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

SheetContent.propTypes = {
    side: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    className: PropTypes.string,
    children: PropTypes.node,
    closeOnOverlayClick: PropTypes.bool,
    onClose: PropTypes.func,
};

const SheetHeader = ({ className, ...props }) => (
    <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

SheetHeader.propTypes = {
    className: PropTypes.string,
};

const SheetFooter = ({ className, ...props }) => (
    <div
        className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
        {...props}
    />
);
SheetFooter.displayName = 'SheetFooter';

SheetFooter.propTypes = {
    className: PropTypes.string,
};

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        id="sheet-title"
        className={cn('text-lg font-semibold text-foreground', className)}
        {...props}
    />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

SheetTitle.propTypes = {
    className: PropTypes.string,
};

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        ref={ref}
        id="sheet-description"
        className={cn('text-sm text-muted-foreground', className)}
        {...props}
    />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

SheetDescription.propTypes = {
    className: PropTypes.string,
};

export {
    Sheet,
    SheetPortal,
    SheetOverlay,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
};
